import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Header from './components/Header/Header';
import Canvas from './components/Canvas/Canvas';
import CanvasList from './components/CanvasList/CanvasList';
import InputBar from './components/InputBar/InputBar';
import HelpButton from './components/HelpButton/HelpButton';
import KeyboardShortcuts from './components/KeyboardShortcuts/KeyboardShortcuts';
import ChatHistory from './components/ChatHistory/ChatHistory';
import MobileMessage from './components/MobileMessage/MobileMessage';
import Settings from './components/Settings/Settings';
import { Toaster, toast } from 'react-hot-toast';
import historyManager from './services/historyManager';
import { ScreenRecordingProvider } from './contexts/ScreenRecordingContext';
import { supabase } from './supabaseClient';
import { conversationsApi } from './services/api';
import { useParams, useNavigate } from 'react-router-dom';

function App() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [activeCanvas, setActiveCanvas] = useState(null);
  const [isCanvasListOpen, setIsCanvasListOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingType, setProcessingType] = useState(null);
  const [isSplitView, setIsSplitView] = useState(() => {
    const storedValue = localStorage.getItem('isSplitView');
    return storedValue === null ? true : storedValue === 'true';
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const inputRef = useRef(null);
  const micButtonRef = useRef(null);
  const formSubmitRef = useRef(null);
  const inputBarActionsRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize mobile state immediately during component creation
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [justUpdatedConversationId, setJustUpdatedConversationId] = useState(null); // State for animation

  // Toggle function for Chat History sidebar
  const toggleChatHistory = () => {
    setIsChatHistoryOpen(!isChatHistoryOpen);
  };

  // Handler to expose form submission
  const handleFormSubmit = () => {
    if (formSubmitRef.current) {
      formSubmitRef.current();
    }
  };

  // Handler for immediate events from canvas
  const handleImmediateCanvasEvent = (eventData) => {
    if (inputBarActionsRef.current) {
      // Construct message from event data
      // Assuming eventData has { canvasId, type, ...otherPayload }
      const canvasId = eventData.canvasId || activeCanvas?.id; // Use activeCanvas if not provided
      const message = `Canvas event (id-${canvasId}): ${eventData.type}\nPayload: ${JSON.stringify(eventData.payload || eventData)}`;
      console.log('Sending immediate canvas event as automatic follow-up:', message);
      inputBarActionsRef.current.sendAutomaticFollowUp(message);
    } else {
      console.error('InputBar actions ref not available to send immediate canvas event.');
    }
  };

  // Handler to update conversation title (e.g., after auto-naming)
  const handleUpdateConversationTitle = (conversationId, newTitle) => {
    console.log(`App: Updating title for ${conversationId} to ${newTitle}`);
    setCurrentConversation(prev => {
      if (prev && prev.id === conversationId) {
        console.log('App: Matched current conversation, updating title.');
        return { ...prev, title: newTitle };
      }
      return prev; // Return previous state if ID doesn't match
    });
    // Dispatch event so ConversationHistory updates its list
    window.dispatchEvent(new CustomEvent('conversationUpdated', { 
      detail: { id: conversationId, title: newTitle }
    }));
    // Trigger animation state
    setJustUpdatedConversationId(conversationId);
    // Reset animation state after a delay (e.g., 1.5 seconds)
    setTimeout(() => {
      setJustUpdatedConversationId(null);
    }, 1500);
    // Note: This only updates the *currently active* conversation title in the App state.
    // The title in the ConversationHistory list is managed separately and should update
    // automatically if it relies on the conversation list fetched from the backend 
    // or if the parent component managing that list receives an update.
  };

  // Handler for when InputBar creates a new conversation
  const handleConversationCreated = (newConversationId) => {
    console.log('App.js: New conversation created with ID:', newConversationId);
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      console.log('User not authenticated, cannot create conversation');
      toast.error('Please sign in to create conversations');
      return;
    }
    
    // Update the state to reflect the new active conversation ID
    // We might not have the full conversation object yet, but we have the ID
    setCurrentConversation(prev => ({ 
      ...(prev || {}), // Keep existing data if any (e.g., title placeholder)
      id: newConversationId 
    }));
    
    // Update URL to include the new conversation ID
    console.log(`Updating URL to /c/${newConversationId}`);
    navigate(`/c/${newConversationId}`, { replace: true });
  };

  // Handle canvas list updates
  const handleCanvasListUpdate = (updatedCanvasList) => {
    console.log('Canvas list updated:', updatedCanvasList);
    if (currentConversation) {
      console.log('Current conversation before update:', currentConversation);
      
      // Check if this is a simplified list or full canvas details
      const isSimplifiedList = updatedCanvasList.length > 0 && 
        (typeof updatedCanvasList[0].position !== 'undefined' || 
         !updatedCanvasList[0].code);
      
      console.log('Is simplified list?', isSimplifiedList);
      
      let updatedDetails;
      
      if (isSimplifiedList) {
        // Create a map of existing canvas details by ID
        const detailsMap = {};
        if (currentConversation.canvas_details) {
          currentConversation.canvas_details.forEach(item => {
            if (item.id) {
              detailsMap[item.id] = item;
            }
          });
        }
        console.log('Details map:', detailsMap);
        
        // Create new canvas_details array with the updated order but preserving all details
        updatedDetails = updatedCanvasList.map(item => {
          // For canvas items, use the existing details if available
          if (item.type === 'canvas' && detailsMap[item.id]) {
            return {
              ...detailsMap[item.id],
              position: item.position
            };
          }
          // For separators or new items, use as is
          return item;
        });
      } else {
        // If we got full details, use them directly
        updatedDetails = updatedCanvasList;
      }
      
      console.log('Updated details:', updatedDetails);
      
      // Create simplified list for canvas_list field if we received full details
      const simplifiedList = isSimplifiedList ? 
        updatedCanvasList : 
        updatedDetails.map((item, idx) => ({
          type: item.type || 'canvas',
          id: item.id,
          position: idx,
          title: item.title
        }));
      
      // Update the conversation with both the new list and the updated details
      const updatedConversation = {
        ...currentConversation,
        canvas_list: simplifiedList,
        canvas_details: updatedDetails
      };
      console.log('Updated conversation:', updatedConversation);
      setCurrentConversation(updatedConversation);
      
      // Also update active canvas if it was renamed
      if (activeCanvas) {
        const updatedCanvas = updatedDetails.find(c => c.id === activeCanvas.id);
        if (updatedCanvas && updatedCanvas.title !== activeCanvas.title) {
          console.log('Updating active canvas title from', activeCanvas.title, 'to', updatedCanvas.title);
          setActiveCanvas(updatedCanvas);
        }
      }
    }
  };

  // Handle sign out event
  React.useEffect(() => {
    const handleSignOut = () => {
      // Clear all application state
      setSelectedPrompt('');
      setActiveCanvas(null);
      setIsCanvasListOpen(false);
      setCurrentConversation(null);
      setIsChatHistoryOpen(false);
      setIsSettingsOpen(false);
      setIsProcessing(false);
      setProcessingType(null);
      setChatMessages([]);
      setIsSpeechEnabled(false);
      // Don't reset speech settings as that's a user preference
      // Clear history manager
      historyManager.clear();
      // Reset active canvas ID
      historyManager.setActiveCanvas(null);
      
      // Remove toast notification
    };

    window.addEventListener('userSignedOut', handleSignOut);
    return () => window.removeEventListener('userSignedOut', handleSignOut);
  }, []);

  // Handle active conversation deletion
  React.useEffect(() => {
    const handleActiveConversationDeleted = () => {
      // Clear all application state, similar to sign out handler
      setSelectedPrompt('');
      setActiveCanvas(null);
      setIsCanvasListOpen(false);
      setCurrentConversation(null);
      setIsChatHistoryOpen(false);
      setIsSettingsOpen(false);
      setIsProcessing(false);
      setProcessingType(null);
      setChatMessages([]);
      // Don't reset speech settings as that's a user preference
      // Clear history manager
      historyManager.clear();
      // Reset active canvas ID
      historyManager.setActiveCanvas(null);
      
      // Navigate to the home route to update the URL in the address bar
      navigate('/');
    };

    window.addEventListener('userDeletedActiveConversation', handleActiveConversationDeleted);
    return () => window.removeEventListener('userDeletedActiveConversation', handleActiveConversationDeleted);
  }, []);

  // Listen for conversation updates
  React.useEffect(() => {
    const handleConversationUpdate = (event) => {
      const updatedConversation = event.detail;
      console.log('Received conversation update:', updatedConversation);
      
      // If this is a different conversation than the current one, clear history
      if (currentConversation && updatedConversation.id !== currentConversation.id) {
        console.log('Switching to different conversation, clearing history');
        historyManager.clear();
        historyManager.setActiveCanvas(null);
      }
      
      // Update the current conversation
      setCurrentConversation(updatedConversation);
      
      // Find and set the last valid canvas as active
      if (updatedConversation.canvas_details?.length > 0) {
        const validCanvases = updatedConversation.canvas_details.filter(c => c.type !== 'separator');
        if (validCanvases.length > 0) {
          const lastCanvas = validCanvases[validCanvases.length - 1];
          console.log('Setting last canvas as active:', lastCanvas);
          
          // Use a longer delay for new conversations to ensure all state updates complete
          const delay = updatedConversation.title === 'New Conversation' ? 100 : 10;
          
          // Ensure we set the active canvas with a delay to allow state updates to complete
          setTimeout(() => {
            console.log('Setting active canvas after delay:', lastCanvas.id);
            setActiveCanvas(lastCanvas);
            // Update the active canvas ID in history manager
            historyManager.setActiveCanvas(lastCanvas.id);
          }, delay);
        }
      } else {
        // Explicitly set activeCanvas to null for conversations without canvases
        console.log('Updated conversation has no canvases, setting activeCanvas to null');
        setActiveCanvas(null);
        historyManager.setActiveCanvas(null);
      }

      // If this update includes a new canvas, store its code
      const lastCanvas = updatedConversation.canvas_details?.slice(-1)[0];
      if (lastCanvas && lastCanvas.code) {
        // Only update the generated canvas code if it's not already set
        // This prevents overwriting the code set by the InputBar component
        if (!historyManager.lastGeneratedCanvasCode) {
          historyManager.setGeneratedCanvasCode(lastCanvas.code);
          // Also set the last created canvas ID if it's not already set
          if (!historyManager.lastCreatedCanvasId) {
            historyManager.setLastCreatedCanvasId(lastCanvas.id);
          }
        }
      }
    };

    window.addEventListener('conversationUpdated', handleConversationUpdate);
    return () => window.removeEventListener('conversationUpdated', handleConversationUpdate);
  }, [currentConversation]);

  // Listen for canvas edits
  React.useEffect(() => {
    const handleCanvasEdit = (event) => {
      const { canvasId, newCode } = event.detail;
      
      // Store the edited code
      historyManager.setEditedCanvasCode(newCode);
      
      // Update the canvas in currentConversation.canvas_details
      if (currentConversation?.canvas_details) {
        const updatedDetails = currentConversation.canvas_details.map(canvas => {
          if (canvas.id === parseInt(canvasId.replace('id-', ''))) {
            return { ...canvas, code: newCode };
          }
          return canvas;
        });

        setCurrentConversation(prev => ({
          ...prev,
          canvas_details: updatedDetails
        }));

        // Update active canvas if it was the one edited
        if (activeCanvas?.id === parseInt(canvasId.replace('id-', ''))) {
          setActiveCanvas(prev => ({ ...prev, code: newCode }));
        }
      }
    };

    window.addEventListener('canvasEdited', handleCanvasEdit);
    return () => window.removeEventListener('canvasEdited', handleCanvasEdit);
  }, [currentConversation, activeCanvas]);

  // Update historyManager when active canvas changes
  useEffect(() => {
    if (activeCanvas?.id) {
      historyManager.setActiveCanvas(activeCanvas.id);
    }
  }, [activeCanvas]);

  const handlePromptSelect = (promptText) => {
    setSelectedPrompt(promptText);
  };

  // Fetch messages for a conversation
  const fetchConversationMessages = async (conversationId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found, skipping message fetch');
        return;
      }

      // Reset message-related state
      setIsLoadingMessages(true);
      setHasMoreMessages(true);
      setChatMessages([]);

      console.log('Fetching initial messages for conversation:', conversationId);
      const messagesResponse = await conversationsApi.getConversationMessages(
        conversationId,
        user.id,
        {
          limit: 20
        }
      );
      
      if (messagesResponse.status === 'success') {
        console.log('Received initial messages:', {
          count: messagesResponse.messages?.length || 0
        });

        if (messagesResponse.messages && messagesResponse.messages.length > 0) {
          // Format messages for chat history
          const formattedMessages = messagesResponse.messages.map(msg => {
            let content;
            
            if (msg.role === 'user') {
              content = msg.content.text || '';
              return {
                id: `${msg.role}-${msg.message_id}`,
                role: msg.role,
                content: content,
                timestamp: parseInt(msg.message_id),
                images: msg.content.images || []
              };
            } else if (msg.role === 'assistant') {
              if (typeof msg.content === 'object' && msg.content !== null) {
                if (msg.content.speak && msg.content.speak.content) {
                  content = {
                    speak: msg.content.speak
                  };
                } else {
                  content = msg.content;
                }
              } else {
                content = msg.content || '';
              }
              
              return {
                id: `${msg.role}-${msg.message_id}`,
                role: msg.role,
                content: content,
                timestamp: parseInt(msg.message_id)
              };
            }
          }).filter(Boolean); // Filter out any undefined messages

          // Update chat messages
          setChatMessages(formattedMessages);
          
          // Set hasMore based on whether we got a full page
          const hasMore = formattedMessages.length === 20;
          setHasMoreMessages(hasMore);
          
          console.log(`Initial messages loaded: ${formattedMessages.length}`, 
                      hasMore ? 'More available' : 'No more available');
          
          // If we have messages but not a full page, we know there are no more
          if (formattedMessages.length > 0 && !hasMore) {
            console.log('Initial fetch has fewer than a full page, setting hasMore=false');
          }
          
        } else {
          // No messages found
          console.log('No messages found for this conversation');
          setHasMoreMessages(false);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load chat history');
      setHasMoreMessages(false);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Handle canvas setup for a conversation
  const setupCanvasForConversation = (conversation) => {
    if (!conversation) return;

    // Find and set the last valid canvas if there are any canvases
    if (conversation.canvas_details?.length > 0) {
      const validCanvases = conversation.canvas_details.filter(c => c.type !== 'separator');
      if (validCanvases.length > 0) {
        const lastCanvas = validCanvases[validCanvases.length - 1];
        console.log('Setting last canvas as active:', lastCanvas);
        
        // Force the state updates to happen in order with a slight delay
        setTimeout(() => {
          setActiveCanvas(lastCanvas);
          // Set the active canvas ID in history manager
          historyManager.setActiveCanvas(lastCanvas.id);
        }, 50);
      }
    } else {
      // Explicitly set activeCanvas to null for conversations without canvases
      console.log('Conversation has no canvases, setting activeCanvas to null');
      setActiveCanvas(null);
      historyManager.setActiveCanvas(null);
    }
  };

  const handleConversationSelect = async (conversation) => {
    console.log('App received conversation:', conversation);
    if (!conversation) {
      console.log('No conversation data, clearing state');
      setCurrentConversation(null);
      setActiveCanvas(null);
      setIsCanvasListOpen(false);
      // Clear history manager
      historyManager.clear();
      // Reset active canvas ID in history manager
      historyManager.setActiveCanvas(null);
      setChatMessages([]);
      // Update URL to home when clearing conversation
      if (conversationId) {
        navigate('/');
      }
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      console.log('User not authenticated, cannot select conversation');
      toast.error('Please sign in to view conversations');
      return;
    }

    // Prevent loading if we're already on this conversation
    if (currentConversation && currentConversation.id === conversation.id) {
      console.log('Already on this conversation, skipping reload');
      return;
    }

    // Clear chat messages when switching conversations
    setChatMessages([]);
    
    // Clear history manager when switching conversations
    historyManager.clear();
    // Reset active canvas ID in history manager
    historyManager.setActiveCanvas(null);
    
    // For new conversations, explicitly clear the active canvas first
    if (conversation.title === 'New Conversation') {
      console.log('New conversation detected, clearing active canvas');
      setActiveCanvas(null);
    }
    
    // First update the URL to prevent multiple loads
    if (!conversationId || parseInt(conversationId) !== conversation.id) {
      console.log(`Updating URL to /c/${conversation.id}`);
      navigate(`/c/${conversation.id}`, { replace: true });
    }
    
    // Set the conversation to trigger a re-render
    setCurrentConversation(conversation);

    // Use a small delay to ensure state updates have propagated
    // This helps prevent race conditions with URL changes and state updates
    setTimeout(() => {
      // Start both operations independently
      setupCanvasForConversation(conversation);
      fetchConversationMessages(conversation.id);
    }, 50);
  };

  const handleCanvasSelect = (canvas) => {
    console.log('App handleCanvasSelect called with:', canvas);
    if (!canvas || canvas?.type === 'separator') {
      console.log('Invalid canvas or separator, ignoring');
      return;
    }
    
    // Ensure canvas ID is a number
    const canvasId = typeof canvas.id === 'string' ? parseInt(canvas.id, 10) : canvas.id;
    const normalizedCanvas = {
      ...canvas,
      id: canvasId
    };
    
    console.log('Setting active canvas:', normalizedCanvas);
    setActiveCanvas(normalizedCanvas);
  };

  const handlePrevCanvas = () => {
    if (currentConversation?.canvas_details?.length > 0) {
      const canvases = currentConversation.canvas_details.filter(c => c.type !== 'separator');
      if (canvases.length === 0) return;
      
      const currentIndex = canvases.findIndex(c => c.id === activeCanvas?.id);
      if (currentIndex > 0) {
        setActiveCanvas(canvases[currentIndex - 1]);
      }
    }
  };

  const handleNextCanvas = () => {
    if (currentConversation?.canvas_details?.length > 0) {
      const canvases = currentConversation.canvas_details.filter(c => c.type !== 'separator');
      if (canvases.length === 0) return;
      
      const currentIndex = canvases.findIndex(c => c.id === activeCanvas?.id);
      if (currentIndex < canvases.length - 1) {
        setActiveCanvas(canvases[currentIndex + 1]);
      }
    }
  };

  const toggleCanvasList = () => {
    console.log('Toggling canvas list, current state:', isCanvasListOpen);
    setIsCanvasListOpen(!isCanvasListOpen);
  };

  // Debug log for active canvas changes
  React.useEffect(() => {
    console.log('Active canvas updated:', activeCanvas);
  }, [activeCanvas]);

  // Debug log for current conversation changes
  React.useEffect(() => {
    console.log('Current conversation updated:', currentConversation);
  }, [currentConversation]);

  // Handle events from canvas iframes - with null check
  const handleCanvasEvent = (event) => {
    console.log('App received canvas event:', event);
    
    if (event.type === 'immediate' && inputRef.current) {
      try {
        const form = inputRef.current?.closest('form');
        if (form) {
          const submitEvent = new SubmitEvent('submit', {
            bubbles: true,
            cancelable: true,
            isTrusted: false
          });
          form.dispatchEvent(submitEvent);
        }
      } catch (error) {
        console.warn('Failed to submit form:', error);
      }
    }
  };

  // Focus the input field when the app loads
  useEffect(() => {
    // Only attempt to focus if we're on desktop
    if (!isMobile) {
      const focusTimer = setTimeout(() => {
        try {
          inputRef.current?.focus();
        } catch (error) {
          console.warn('Failed to focus input:', error);
        }
      }, 300);
      
      return () => clearTimeout(focusTimer);
    }
  }, []); // Empty dependency array as we check isMobile in the effect

  // Function to focus the input field - with better error handling
  const focusInput = () => {
    try {
      if (!isMobile && inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.warn('Failed to focus input:', error);
    }
  };

  // Function to toggle the microphone
  const toggleMic = () => {
    if (micButtonRef.current) {
      micButtonRef.current.click();
    }
  };

  // Function to close all panels
  const closePanels = () => {
    setIsCanvasListOpen(false);
    setIsChatHistoryOpen(false);
    setIsSettingsOpen(false);
  };

  // Handle chat message addition
  const handleNewMessage = (message) => {
    setChatMessages(prev => [...prev, message]);
    // Open chat history when speech is disabled
    if (!isSpeechEnabled && message.role === 'assistant' && !isSplitView) {
      setIsChatHistoryOpen(true);
    }
  };

  // Handle speech toggle
  const handleSpeechToggle = (enabled) => {
    setIsSpeechEnabled(enabled);
    // Store preference
    localStorage.setItem('speechEnabled', enabled.toString());
  };

  // Load speech preference
  useEffect(() => {
    const speechEnabled = localStorage.getItem('speechEnabled') === 'true';
    setIsSpeechEnabled(speechEnabled);
  }, []);

  const handleProcessingChange = (isProcessing, type = null) => {
    console.log('Processing state changed:', { isProcessing, type });
    setIsProcessing(isProcessing);
    setProcessingType(isProcessing ? (type || 'conversation') : null);
  };

  // Update mobile state on resize
  useEffect(() => {
    const checkScreenSize = () => {
      const isCurrentlyMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const screenWidth = window.innerWidth;
      
      if (isCurrentlyMobile !== isMobile) {
          setIsMobile(isCurrentlyMobile);
      }

      // Check if screen is too small for split view
      if (screenWidth < 1024 && isSplitView) {
        console.log(`Screen width ${screenWidth}px < 1024px, disabling split view.`);
        setIsSplitView(false); // Turn off split view
        localStorage.setItem('isSplitView', 'false'); // Update local storage
        // Use toast directly if available, ensure ToastContainer is rendered
        toast('Split view disabled on smaller screens.', { duration: 3000 });
      }
    };

    checkScreenSize(); // Initial check
    // Run on mount and window resize
    window.addEventListener('resize', checkScreenSize);
    // Cleanup listener
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [isMobile, isSplitView, setIsSplitView]); // Add isSplitView and setIsSplitView dependencies

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsAuthenticated(!!data.session);
        setAuthChecked(true);
        console.log('Auth check completed, user is', data.session ? 'authenticated' : 'not authenticated');
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setAuthChecked(true);
      }
    };
    
    checkAuth();
    
    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      console.log('Auth state changed:', event, 'User is', session ? 'authenticated' : 'not authenticated');
    });
    
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Load conversation based on URL parameter
  useEffect(() => {
    // Create a flag to track if this effect is still relevant
    let isMounted = true;
    
    const loadConversationFromUrl = async () => {
      // Skip if any required conditions are not met
      if (!conversationId || !isAuthenticated || !authChecked) return;
      
      // Skip if we're already on the correct conversation
      if (currentConversation && currentConversation.id === parseInt(conversationId)) {
        console.log('Already on the correct conversation, skipping URL-based load');
        return;
      }
      
      try {
        const { data } = await supabase.auth.getUser();
        // Check if component is still mounted and conditions are still valid
        if (!isMounted) return;
        
        if (!data || !data.user) {
          console.log('User not authenticated, cannot load conversation');
          return;
        }
        
        const userId = data.user.id;
        console.log('Loading conversation from URL, ID:', conversationId, 'User ID:', userId);
        
        // Fetch conversation details
        const response = await conversationsApi.getConversationDetails(conversationId, userId);
        // Check if component is still mounted
        if (!isMounted) return;
        
        console.log('Conversation details response:', response);
        
        if (response.status === 'success') {
          console.log('Successfully loaded conversation from URL:', response.conversation);
          // Use direct state updates instead of handleConversationSelect to avoid loops
          setCurrentConversation(response.conversation);
          setChatMessages([]);
          historyManager.clear();
          historyManager.setActiveCanvas(null);
          
          // Use setTimeout to ensure state updates have propagated
          setTimeout(() => {
            if (isMounted) {
              setupCanvasForConversation(response.conversation);
              fetchConversationMessages(response.conversation.id);
            }
          }, 50);
        } else {
          console.error('Failed to load conversation:', response.message || 'Unknown error');
          toast.error('Failed to load conversation');
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading conversation from URL:', error);
          toast.error('Failed to load conversation');
        }
      }
    };
    
    // Add a small delay to avoid race conditions with other effects
    const timeoutId = setTimeout(() => {
      loadConversationFromUrl();
    }, 100);
    
    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [conversationId, isAuthenticated, authChecked]);

  // Handle loading more messages
  useEffect(() => {
    const loadMoreMessages = async (event) => {
      const { oldestMessageId } = event.detail;
      
      // If we don't have a current conversation, exit
      if (!currentConversation?.id) {
        console.log('No active conversation, cannot load more messages');
        return;
      }
      
      // If we're already loading or know there are no more messages
      if (isLoadingMessages || !hasMoreMessages) {
        console.log('App: Load more messages requested but', 
          isLoadingMessages ? 'already loading' : 'no more messages available');
        
        // If we know there are no more messages, ALWAYS explicitly notify component
        // This is critical for showing the "No more messages" indicator
        if (!hasMoreMessages) {
          console.log('App: Notifying chat history that there are no more messages (ID: ' + oldestMessageId + ')');
          
          // Set a very short delay to ensure ChatHistory has fully rendered
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('newMessagesLoaded', {
              detail: {
                newMessages: [],
                hasMore: false
              }
            }));
          }, 10);
        }
        return;
      }
      
      // If we don't have an oldest message ID to paginate from, exit
      if (!oldestMessageId) {
        console.log('App: Missing oldestMessageId, cannot load more messages');
        return;
      }

      try {
        setIsLoadingMessages(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const messagesResponse = await conversationsApi.getConversationMessages(
            currentConversation.id,
            user.id,
            {
              before: oldestMessageId,
              limit: 20
            }
          );

          if (messagesResponse.status === 'success') {
            if (messagesResponse.messages && messagesResponse.messages.length > 0) {
              const formattedMessages = messagesResponse.messages.map(msg => {
                let content;
                
                if (msg.role === 'user') {
                  content = msg.content.text || '';
                  return {
                    id: `${msg.role}-${msg.message_id}`,
                    role: msg.role,
                    content: content,
                    timestamp: parseInt(msg.message_id),
                    images: msg.content.images || []
                  };
                } else if (msg.role === 'assistant') {
                  if (typeof msg.content === 'object' && msg.content !== null) {
                    if (msg.content.speak && msg.content.speak.content) {
                      content = {
                        speak: msg.content.speak
                      };
                    } else {
                      content = msg.content;
                    }
                  } else {
                    content = msg.content || '';
                  }
                  
                  return {
                    id: `${msg.role}-${msg.message_id}`,
                    role: msg.role,
                    content: content,
                    timestamp: parseInt(msg.message_id)
                  };
                }
              }).filter(Boolean); // Filter out any undefined messages

              if (formattedMessages.length > 0) {
                // Update state immutably by prepending new messages
                setChatMessages(prevMessages => [...formattedMessages, ...prevMessages]);
              }

              // Update hasMore state
              setHasMoreMessages(formattedMessages.length === 20);
            } else {
              // No messages in response
              handleNoMoreMessages();
            }
          } else {
            // Error in response
            handleNoMoreMessages();
          }
        }
      } catch (error) {
        console.error('Error loading more messages:', error);
        setIsLoadingMessages(false);
        toast.error('Failed to load more messages');
        // Reset loading state and notify ChatHistory component
        window.dispatchEvent(new CustomEvent('newMessagesLoaded', {
          detail: {
            newMessages: [],
            hasMore: true // Keep hasMore true so user can retry
          }
        }));
      } finally {
        setIsLoadingMessages(false);
      }
    };

    // Helper function to handle when no more messages are available
    const handleNoMoreMessages = () => {
      setHasMoreMessages(false);
      setIsLoadingMessages(false);
      // Dispatch event to notify ChatHistory component
      window.dispatchEvent(new CustomEvent('newMessagesLoaded', {
        detail: {
          newMessages: [],
          hasMore: false
        }
      }));
      // Ensure map returns a value
      return null; // Or return [], if map is expected to produce an array
    };

    window.addEventListener('loadMoreMessages', loadMoreMessages);
    return () => window.removeEventListener('loadMoreMessages', loadMoreMessages);
  }, [currentConversation?.id, isLoadingMessages, hasMoreMessages, isSpeechEnabled, isSplitView]);

  // Effect to save split view state to localStorage
  useEffect(() => {
    localStorage.setItem('isSplitView', isSplitView);
    // If split view is turned on, ensure the sidebar chat history is closed
    if (isSplitView) {
      setIsChatHistoryOpen(false);
    }
  }, [isSplitView]);

  // If mobile device, show mobile message early
  if (isMobile) {
    return <MobileMessage />;
  }

  return (
    <ScreenRecordingProvider>
      <div className="app">
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#333',
              color: '#fff',
              padding: '16px',
              borderRadius: '10px',
              fontSize: '14px'
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff'
              }
            }
          }}
        />
        <Header 
          onCanvasListToggle={toggleCanvasList}
          onConversationSelect={handleConversationSelect}
          onChatHistoryToggle={(forceState) => {
            if (typeof forceState === 'boolean') {
              // Only allow setting if split view is off
               if (!isSplitView) {
                 setIsChatHistoryOpen(forceState);
               }
            } else {
              // Only allow toggle if split view is off
              if (!isSplitView) {
                  setIsChatHistoryOpen(!isChatHistoryOpen);
              }
            }
          }}
          onSettingsToggle={(forceState) => {
            if (typeof forceState === 'boolean') {
              setIsSettingsOpen(forceState);
            } else {
              setIsSettingsOpen(!isSettingsOpen);
            }
          }}
          isChatHistoryOpen={isChatHistoryOpen}
          isSettingsOpen={isSettingsOpen}
          justUpdatedConversationId={justUpdatedConversationId} // Pass down animation state
          isSplitViewEnabled={isSplitView} // Pass split view status to header
        />
        {/* Apply split-view-active class conditionally */}
        <main className={`main ${isSplitView ? 'split-view-active' : ''}`}>
          {/* Content Area holds Canvas and potentially ChatHistory */}
          <div className="content-area">
             {/* Wrapper for Canvas */}
            <div className="canvas-area">
              <Canvas 
                onPromptSelect={handlePromptSelect}
                activeCanvas={activeCanvas}
                onPrevCanvas={handlePrevCanvas}
                onNextCanvas={handleNextCanvas}
                onCanvasEvent={handleCanvasEvent}
                isProcessing={isProcessing}
                processingType={processingType}
                currentConversation={currentConversation}
                onSubmit={handleFormSubmit}
                onImmediateEvent={handleImmediateCanvasEvent}
                onFocusChange={(canvasId, isFocused) => {
                  // Handle focus change if needed, e.g., for styling or keyboard events
                  console.log(`Canvas ${canvasId} focus changed: ${isFocused}`);
                }}
              />
            </div>
            {/* Conditionally render inline Chat History */}
            {isSplitView && (
              <div className="chat-history-area">
                <ChatHistory
                  isInline={true} // New prop to indicate inline mode
                  messages={chatMessages}
                  isSpeechEnabled={isSpeechEnabled}
                  isLoadingInitialMessages={isLoadingMessages}
                  // Pass other necessary props like conversation ID if needed
                />
              </div>
            )}
          </div>
           {/* InputBar is now a direct child of main, after content-area */}
          <InputBar
              ref={inputBarActionsRef}
              selectedPrompt={selectedPrompt}
              inputRef={inputRef}
              micButtonRef={micButtonRef}
              activeConversationId={currentConversation?.id}
              activeCanvasId={activeCanvas?.id}
              currentConversation={currentConversation}
              onProcessingChange={handleProcessingChange}
              onCanvasSwitch={handleCanvasSelect}
              isSpeechEnabled={isSpeechEnabled}
              onSpeechToggle={handleSpeechToggle}
              onNewMessage={handleNewMessage}
              onUpdateConversationTitle={handleUpdateConversationTitle}
              onConversationCreated={handleConversationCreated}
              formSubmitRef={formSubmitRef}
            />
        </main>
        {/* CanvasList sidebar - moved outside main */}
        <CanvasList 
          isOpen={isCanvasListOpen}
          onClose={toggleCanvasList}
          conversation={currentConversation}
          onCanvasSelect={handleCanvasSelect}
          onCanvasListUpdate={handleCanvasListUpdate}
          activeCanvas={activeCanvas}
        />
        {/* Settings Sidebar - moved outside main */}
        <Settings
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          isSplitView={isSplitView} // Pass state
          onSplitViewChange={setIsSplitView} // Pass setter
          // Pass other existing props like onAutoStartMicChange
          // Make sure to get existing props if Settings needs them
          onAutoStartMicChange={(value) => console.log('AutoStartMic changed:', value)} // Example placeholder
        />
        {/* HelpButton and KeyboardShortcuts remain siblings to main */}
        <HelpButton />
        <KeyboardShortcuts 
          onFocusInput={focusInput}
          onToggleMic={toggleMic}
          onToggleCanvasList={toggleCanvasList}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onPrevCanvas={handlePrevCanvas}
          onNextCanvas={handleNextCanvas}
          onClosePanels={closePanels}
          onOpenChatHistory={() => {
            // Only open sidebar if split view is off
            if (!isSplitView) {
              setIsChatHistoryOpen(true);
            }
          }}
        />
        {/* Render sidebar ChatHistory only if split view is OFF */}
        {!isSplitView && (
          (() => { // IIFE to allow logging before returning JSX
            console.log('[App.js] Rendering sidebar ChatHistory with messages length:', chatMessages.length);
            return (
              <ChatHistory
                isOpen={isChatHistoryOpen}
                onClose={toggleChatHistory}
                messages={chatMessages}
                isSpeechEnabled={isSpeechEnabled}
                isLoadingInitialMessages={isLoadingMessages}
                isLoadingMore={isLoadingMessages}
                hasMore={hasMoreMessages}
              />
            );
          })()
        )}
      </div>
    </ScreenRecordingProvider>
  );
}

export default App;
